import { useEffect, useLayoutEffect, useRef } from 'react'

import 'root-redux/actions/user'
import {
  selectGoogleAdsConversions,
  selectGoogleAdsId,
  selectGoogleAnalyticsId,
} from 'root-redux/selects/common'

import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { useBulkSelector } from './useBulkSelector'
import { useCookieConsentAnswer } from './useCookieConsentAnswer'

const enum GAConsent {
  DENIED = 'denied',
  GRANTED = 'granted',
}

const useSelectors = () =>
  useBulkSelector({
    googleAnalyticsId: selectGoogleAnalyticsId,
    googleAdsId: selectGoogleAdsId,
    googleAdsConversions: selectGoogleAdsConversions,
  })

const ensureGtag = () => {
  if (!window.gtag) {
    const script = document.createElement('script')
    script.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
    `

    document.head.appendChild(script)
  }
}

export const useGoogleAnalytics = (): void => {
  const { googleAnalyticsId, googleAdsConversions, googleAdsId } =
    useSelectors()
  const {
    hasAdvertisingConsent,
    hasAnalyticsConsent,
    hasAnsweredConsent,
    shouldApplyCookieConsent,
  } = useCookieConsentAnswer()
  const isDefaultConsentSetRef = useRef(false)

  useLayoutEffect(() => {
    if (!shouldApplyCookieConsent || isDefaultConsentSetRef.current) {
      return
    }

    ensureGtag()

    window.gtag('consent', 'default', {
      ad_storage: GAConsent.DENIED,
      ad_user_data: GAConsent.DENIED,
      ad_personalization: GAConsent.DENIED,
      analytics_storage: GAConsent.DENIED,
    })

    isDefaultConsentSetRef.current = true
  }, [shouldApplyCookieConsent])

  useLayoutEffect(() => {
    if (!shouldApplyCookieConsent || !hasAnsweredConsent) {
      return
    }

    ensureGtag()

    window.gtag('consent', 'update', {
      ad_storage: hasAdvertisingConsent ? GAConsent.GRANTED : GAConsent.DENIED,
      ad_user_data: hasAdvertisingConsent
        ? GAConsent.GRANTED
        : GAConsent.DENIED,
      ad_personalization: hasAdvertisingConsent
        ? GAConsent.GRANTED
        : GAConsent.DENIED,
      analytics_storage: hasAnalyticsConsent
        ? GAConsent.GRANTED
        : GAConsent.DENIED,
    })
  }, [
    hasAdvertisingConsent,
    hasAnalyticsConsent,
    hasAnsweredConsent,
    shouldApplyCookieConsent,
  ])

  useEffect(() => {
    if (!googleAdsId || !googleAdsConversions) return

    const initGtag = () => {
      ensureGtag()
      window.gtag('js', new Date())
    }

    const setUpAdsGTM = () => {
      const script = document.createElement('script')
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`
      script.async = true
      document.body.appendChild(script)
    }

    googleAnalyticsLogger.configure(googleAdsConversions)
    initGtag()
    setUpAdsGTM()
  }, [googleAdsId, googleAdsConversions])

  useEffect(() => {
    if (!googleAnalyticsId) return

    const initGtag = () => {
      ensureGtag()
      window.gtag('js', new Date())
    }

    const setUpGTM = () => {
      const script = document.createElement('script')
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`
      script.async = true
      document.head.appendChild(script)
    }

    initGtag()
    setUpGTM()
    window.gtag('config', googleAnalyticsId, { send_page_view: false })
  }, [googleAnalyticsId])
}
